/* eslint-disable no-unused-vars */
import _ from 'underscore';
import {
  antiForgeryTokenKey,
  getAntiForgeryToken,
} from '@bingads-webui-clientcenter/restful-service';
import type { AdditionalFormDataValue, IErrorResponse, IUploadFileOptions } from './types';

const defaultErrorHandler = (request: XMLHttpRequest): string => {
  const { response, status, statusText } = request;

  const errorResponse = response as IErrorResponse;

  let error = '';
  if (status !== 200) { // analyze HTTP status of the response
    error = `Error ${status}: ${statusText}`;
  } else if (!response) {
    error = 'No response from server';
  } else if (errorResponse.Errors) {
    // errors caused by uncaught exceptions (generic error message)
    // or error response from the controller
    error = errorResponse.Errors[0].Message;
  }

  return error;
};

export const uploadFile = ({
  file,
  id,
  additionalFormData,
  endPoint,
  onUploadProgress = _.noop,
  onSuccess = _.noop,
  onError = _.noop,
  onSuccessErrorHandler = defaultErrorHandler,
}: IUploadFileOptions): void => {
  const formData = new FormData();
  const antiForgeryToken = getAntiForgeryToken();
  /* istanbul ignore if */
  if (antiForgeryToken) {
    formData.append(antiForgeryTokenKey, antiForgeryToken);
  }
  formData.append('file', file);
  formData.append('id', id);
  if (!_.isEmpty(additionalFormData)) {
    _.each(additionalFormData, (value: AdditionalFormDataValue, key: string) => {
      formData.append(key, value);
    });
  }

  const request = new XMLHttpRequest();
  request.open('POST', endPoint);
  request.responseType = 'json';
  request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
  request.withCredentials = true;

  request.upload.onprogress = (event: ProgressEvent): void => {
    const { lengthComputable, loaded, total } = event;
    /* istanbul ignore next */
    const progress = lengthComputable ? loaded / total : 0;
    const completed = loaded === total;
    onUploadProgress({
      id,
      completed,
      progress,
      loaded,
      total,
      abort: /* istanbul ignore next */ (): void => {
        if (!completed) {
          request.abort();
        }
      },
    });
  };

  request.onload = (): void => {
    const { response, status } = request;
    const error = onSuccessErrorHandler(request);

    if (error) {
      onError({
        id,
        response,
        status,
        error,
      });
    } else {
      onSuccess({
        id,
        response,
        status,
      });
    }
  };

  /* istanbul ignore next */
  request.onerror = (): void => {
    const { response, status } = request;
    onError({
      id,
      response,
      status,
      error: 'Request failed',
    });
  };

  request.send(formData);
};
